"use client"

import { createContext, useContext, useState, useEffect } from "react"
import BASE_URL from '../config';

const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  useEffect(() => {
    // Check if user is logged in on app start
    const token = localStorage.getItem("token")
    const userData = localStorage.getItem("user")

    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData)
        setUser(parsedUser)
        setIsAuthenticated(true)
      } catch (error) {
        console.error("Error parsing user data:", error)
        localStorage.removeItem("token")
        localStorage.removeItem("user")
      }
    }
  }, [])

  const login = async (credentials) => {
    // If Google OAuth or no password, just set user and auth state
    if (credentials?.isOAuth || !credentials?.password) {
      if (credentials?.user) {
        localStorage.setItem("token", credentials.token || "")
        localStorage.setItem("user", JSON.stringify(credentials.user))
        setUser(credentials.user)
        setIsAuthenticated(true)
        return credentials.user
      }
      // fallback: set from username/email if user object not present
      setUser({
        username: credentials.username,
        email: credentials.email,
        role: credentials.role,
      })
      setIsAuthenticated(true)
      return { username: credentials.username, email: credentials.email, role: credentials.role }
    }

    try {
      const response = await fetch(`${BASE_URL}/api/auth/login/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username: credentials.username,
          password: credentials.password,
        }),
      })

      const data = await response.json()

      if (response.ok) {
        localStorage.setItem("token", data.token)
        localStorage.setItem("user", JSON.stringify(data.user))
        setUser(data.user)
        setIsAuthenticated(true)
        return data.user
      } else {
        throw new Error(data.message || "Login failed")
      }
    } catch (error) {
      console.error("Login error:", error)
      throw error
    }
  }

  const logout = async () => {
    try {
      const token = localStorage.getItem("token")
      if (token) {
        await fetch(`${BASE_URL}/api/auth/logout/`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        })
      }
    } catch (error) {
      console.error("Logout error:", error)
    } finally {
      localStorage.removeItem("token")
      localStorage.removeItem("user")
      setUser(null)
      setIsAuthenticated(false)
    }
  }

  const isAdmin = user?.role === "admin"
  const isClerk = user?.role === "clerk"
  const isCustomer = user?.role === "customer"

  const value = {
    user,
    isAuthenticated,
    login,
    logout,
    isAdmin,
    isClerk,
    isCustomer,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
